// Phase 12 (ADR-015) — Movement Zod schemas shared between backend and
// mobile. A `Movement` is a physical movement of one or more animals; it
// owns the marketplace `IN_TRANSIT` phase and also covers a FARMER's own
// moves between holdings or towards a fair.
//
// The creator (FARMER on an own move, seller on a marketplace sale) generates
// a single-use `invite_code`; the TRANSPORTISTA redeems it and is assigned.
// Pickup / delivery produce TRANSPORT_PICKUP / TRANSPORT_DELIVERY events (one
// per animal) grouped through an EventBatch — see event.zod.ts.
import { z } from 'zod';
/** Lifecycle of a Movement. Mirrors the Prisma `MovementStatus` enum.
 *   DRAFT      — created, no transporter assigned
 *   ASSIGNED   — a transporter redeemed the invite code
 *   PICKED_UP  — TRANSPORT_PICKUP recorded for the animals
 *   DELIVERED  — TRANSPORT_DELIVERY recorded for the animals
 *   CANCELLED  — aborted by the creator before delivery */
export const MovementStatusSchema = z.enum([
    'DRAFT',
    'ASSIGNED',
    'PICKED_UP',
    'DELIVERED',
    'CANCELLED',
]);
/** Wire shape of a Movement. `dest_farm_id` is null for an external
 *  destination (fair, slaughterhouse); `dest_label` is the free-text
 *  fallback and `dest_rega_code` the REGA of the destination holding. */
export const MovementSchema = z.object({
    id: z.string().uuid(),
    origin_farm_id: z.string().uuid(),
    dest_farm_id: z.string().uuid().nullable(),
    dest_label: z.string().nullable(),
    dest_rega_code: z.string().nullable(),
    transporter_id: z.string().uuid().nullable(),
    invite_code: z.string().nullable(),
    invite_expires_at: z.string().datetime({ offset: true }).nullable(),
    /** Non-null when the movement originates from a marketplace sale. */
    sale_id: z.string().uuid().nullable(),
    status: MovementStatusSchema,
    created_by: z.string().uuid(),
    animal_ids: z.array(z.string().uuid()),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
});
/** `POST /api/v1/movements` — create a movement. Exactly one of
 *  `dest_farm_id` / `dest_label` identifies the destination. `sale_id` is
 *  set by the server when the movement is spawned from a marketplace sale,
 *  never trusted from the client here. */
export const CreateMovementRequestSchema = z
    .object({
    origin_farm_id: z.string().uuid(),
    dest_farm_id: z.string().uuid().optional(),
    dest_label: z.string().min(1).max(160).optional(),
    dest_rega_code: z.string().min(1).max(40).optional(),
    animal_ids: z.array(z.string().uuid()).min(1).max(200),
})
    .strict()
    .refine((v) => !!v.dest_farm_id || !!v.dest_label, {
    message: 'either dest_farm_id or dest_label is required',
    path: ['dest_farm_id'],
});
/** `POST /api/v1/transporter/movement-links` — a TRANSPORTISTA redeems a
 *  single-use movement invite code (7-day TTL, see INVITE_CODE_TTL_SECONDS). */
export const RedeemMovementInviteRequestSchema = z
    .object({
    invite_code: z.string().min(6).max(64),
})
    .strict();
/** One animal's scan inside a pickup / delivery batch. `leyrospot_uuid` is the
 *  NFC tag scanned to prove custody; GPS is captured on-device and may be
 *  absent when the transporter is fully offline without a fix. */
export const MovementScanSchema = z
    .object({
    animal_id: z.string().uuid(),
    leyrospot_uuid: z.string().uuid().optional(),
    gps_lat: z.number().min(-90).max(90).optional(),
    gps_lng: z.number().min(-180).max(180).optional(),
})
    .strict();
/** `POST /api/v1/movements/:id/pickup` — records TRANSPORT_PICKUP for each
 *  scanned animal. `vehicle_plate` is required here (it lands in every
 *  TRANSPORT_PICKUP `data_json`). */
export const MovementPickupRequestSchema = z
    .object({
    vehicle_plate: z.string().min(1).max(16),
    photo_url: z.string().url().optional(),
    scans: z.array(MovementScanSchema).min(1).max(200),
})
    .strict();
/** `POST /api/v1/movements/:id/delivery` — records TRANSPORT_DELIVERY for
 *  each scanned animal. */
export const MovementDeliveryRequestSchema = z
    .object({
    scans: z.array(MovementScanSchema).min(1).max(200),
})
    .strict();
/** Minimal animal projection embedded inside the movement detail / list
 *  responses. Enough for the transporter UI to show a list of crotals with
 *  optional NFC association indicator without a second roundtrip. */
export const MovementAnimalSummarySchema = z.object({
    id: z.string().uuid(),
    crotal_id: z.string(),
    leyrospot_uuid: z.string().uuid().nullable(),
    name: z.string().nullable(),
});
/** Light party projection (creator / transporter) — the UI only needs name
 *  + id to render. Email / phone are not exposed cross-role for privacy. */
export const MovementPartySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
/** Light farm projection — used both for `origin_farm` and `dest_farm`. */
export const MovementFarmSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    municipality: z.string(),
    rega_code: z.string().nullable(),
});
/** Wire shape of `GET /api/v1/movements/:id` and the items inside the
 *  list responses. Combines `MovementSchema` with the populated relations
 *  the UI needs. */
export const MovementDetailSchema = z.object({
    id: z.string().uuid(),
    origin_farm_id: z.string().uuid(),
    origin_farm: MovementFarmSchema,
    dest_farm_id: z.string().uuid().nullable(),
    dest_farm: MovementFarmSchema.nullable(),
    dest_label: z.string().nullable(),
    dest_rega_code: z.string().nullable(),
    transporter_id: z.string().uuid().nullable(),
    transporter: MovementPartySchema.nullable(),
    invite_code: z.string().nullable(),
    invite_expires_at: z.string().datetime({ offset: true }).nullable(),
    sale_id: z.string().uuid().nullable(),
    status: MovementStatusSchema,
    created_by: z.string().uuid(),
    creator: MovementPartySchema,
    animals: z.array(MovementAnimalSummarySchema),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
});
/** Query params shared by both list endpoints. Cursor-based pagination keyed
 *  by the previous page's last `Movement.id` (rows are ordered by
 *  `created_at desc`, with `id` tie-break for stability). `limit` defaults to
 *  20 and is capped at 100 to keep the response bounded — without this the
 *  home screen of a long-tenured farmer / transporter would pay an unbounded
 *  payload + heavy include cost on every refresh (perf gate finding from
 *  phase-12 iter-2 review). */
export const MovementListQuerySchema = z
    .object({
    cursor: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
})
    .strict();
/** Default page size if the client does not pass `?limit=`. Picked to comfortably
 *  fill the first viewport on a phone (≈8-12 rows) with one batch of look-ahead. */
export const MOVEMENT_LIST_DEFAULT_LIMIT = 20;
/** Hard upper bound — keeps payload + DB-include cost predictable. */
export const MOVEMENT_LIST_MAX_LIMIT = 100;
/** `GET /api/v1/movements` (FARMER) and `GET /api/v1/transporter/movements`.
 *  `next_cursor` is non-null when the server held back further rows past
 *  `limit`; the client passes it as `?cursor=` on the next call. */
export const MovementListResponseSchema = z.object({
    movements: z.array(MovementDetailSchema),
    next_cursor: z.string().uuid().nullable(),
});
//# sourceMappingURL=movement.js.map